import { Grid } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useOnClickOutside, useTernaryDarkMode } from "usehooks-ts";

import Logo from "@/components/Logo";
import { NAMESPACES } from "@/constants/i18next.constants";

import packageJson from "../../../../../package.json";
import CustomTrigger from "../../components/CustomTrigger";
import { SIDEBAR_WIDTH } from "../../MainLayout.constants";
import { useMainLayoutStore } from "../../MainLayout.store";
import { useGetSidebarItems } from "./Sidebar.hooks";
import {
  SiderStyled,
  VersionContainer,
  VersionContainerInner,
  VersionText,
} from "./Sidebar.styles";
import {
  LogoContainer,
  MenuContainer,
  SiderContent,
  StyledMenu,
} from "./Sidebar.styles";

const { useBreakpoint } = Grid;

function Sidebar() {
  const { t } = useTranslation([NAMESPACES.LAYOUT, NAMESPACES.COMMON]);
  const { isDarkMode } = useTernaryDarkMode();
  const { pathname } = useLocation();
  const items = useGetSidebarItems();
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useMainLayoutStore();
  const { xs } = useBreakpoint();
  const ref = useRef(null);

  const [openedKeys, setOpenedKeys] = useState<string[]>([]);

  const onOpenChange = (keys: string[]) => {
    const rootSubmenuKeys = items.map(item => item?.key);

    const latestOpenKey = keys.find(
      key => openedKeys.findIndex(val => key === val) === -1,
    );
    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenedKeys(keys);
    }
    else {
      setOpenedKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const handleClickOutside = () => {
    if (xs && !isSidebarCollapsed) {
      setIsSidebarCollapsed(true);
    }
  };

  useEffect(() => {
    const activeMenuGroupKey = items
      .find(
        item =>
          item?.type === "submenu"
          && item.children?.findIndex(child => child?.key === pathname) > -1,
      )
      ?.key?.toString();

    if (activeMenuGroupKey) {
      setOpenedKeys([activeMenuGroupKey]);
      return;
    }
  }, [items, pathname]);

  useEffect(() => {
    if (xs && !isSidebarCollapsed) {
      document.body.style.overflowY = "hidden";
    }
    else {
      document.body.style.overflowY = "initial";
    }
  }, [xs, isSidebarCollapsed]);

  useOnClickOutside(ref, handleClickOutside);

  return (
    <SiderStyled
      ref={ref}
      collapsedWidth={xs ? 0 : 80}
      breakpoint="md"
      theme={isDarkMode ? "dark" : "light"}
      collapsible
      onCollapse={setIsSidebarCollapsed}
      collapsed={isSidebarCollapsed}
      width={SIDEBAR_WIDTH}
      trigger={null}
    >
      <SiderContent>
        <LogoContainer $collapsed={isSidebarCollapsed}>
          {!isSidebarCollapsed && <Logo width="112" />}
          <CustomTrigger
            collapsed={isSidebarCollapsed}
            onCollapse={setIsSidebarCollapsed}
          />
        </LogoContainer>
        <MenuContainer>
          <StyledMenu
            mode="inline"
            defaultSelectedKeys={[pathname]}
            selectedKeys={[pathname]}
            items={items}
            openKeys={openedKeys}
            onOpenChange={onOpenChange}
          />
        </MenuContainer>
        <VersionContainer>
          <VersionContainerInner>
            <VersionText variant="small" weight="medium">
              {isSidebarCollapsed
                ? `v${packageJson.version}`
                : `${t("eotPlatform", {
                  ns: NAMESPACES.COMMON,
                })} v${packageJson.version} | BETA`}
            </VersionText>
          </VersionContainerInner>
        </VersionContainer>
      </SiderContent>
    </SiderStyled>
  );
}

export default Sidebar;
